import React from "react";
import { Link } from "react-router-dom";

const processInline = (text: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    const boldItalicMatch = remaining.match(/\*\*\*([^*]+)\*\*\*/);
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
    const italicMatch = remaining.match(/\*([^*]+)\*/);
    const codeMatch = remaining.match(/`([^`]+)`/);
    const strikeMatch = remaining.match(/~~([^~]+)~~/);

    const matches = [
      linkMatch ? { type: "link", match: linkMatch } : null,
      boldItalicMatch ? { type: "bolditalic", match: boldItalicMatch } : null,
      boldMatch ? { type: "bold", match: boldMatch } : null,
      italicMatch ? { type: "italic", match: italicMatch } : null,
      codeMatch ? { type: "code", match: codeMatch } : null,
      strikeMatch ? { type: "strike", match: strikeMatch } : null,
    ]
      .filter(Boolean)
      .sort((a, b) => (a!.match!.index || 0) - (b!.match!.index || 0));

    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }

    const first = matches[0]!;
    const idx = first.match!.index!;

    if (idx > 0) parts.push(remaining.substring(0, idx));

    switch (first.type) {
      case "link": {
        const href = first.match![2];
        parts.push(
          href.startsWith("/") ? (
            <Link key={key++} to={href} className="text-primary hover:underline">
              {first.match![1]}
            </Link>
          ) : (
            <a key={key++} href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
              {first.match![1]}
            </a>
          )
        );
        break;
      }
      case "bolditalic":
        parts.push(<strong key={key++}><em>{first.match![1]}</em></strong>);
        break;
      case "bold":
        parts.push(<strong key={key++}>{first.match![1]}</strong>);
        break;
      case "italic":
        parts.push(<em key={key++}>{first.match![1]}</em>);
        break;
      case "code":
        parts.push(
          <code key={key++} className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono">
            {first.match![1]}
          </code>
        );
        break;
      case "strike":
        parts.push(<del key={key++}>{first.match![1]}</del>);
        break;
    }

    remaining = remaining.substring(idx + first.match![0].length);
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
};

export function renderMarkdown(content: string): React.ReactNode[] {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let inCodeBlock = false;
  let codeContent = "";
  let codeLang = "";

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Code blocks
    if (line.startsWith("```")) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${i}`} className="p-4 rounded-lg bg-muted overflow-x-auto my-4">
            <code className="text-sm font-mono" data-lang={codeLang}>{codeContent.trim()}</code>
          </pre>
        );
        codeContent = "";
        codeLang = "";
        inCodeBlock = false;
      } else {
        codeLang = line.slice(3).trim();
        inCodeBlock = true;
      }
      i++;
      continue;
    }

    if (inCodeBlock) {
      codeContent += line + "\n";
      i++;
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      elements.push(<hr key={`hr-${i}`} className="my-8 border-border" />);
      i++;
      continue;
    }

    // Empty line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Blockquote (collect consecutive > lines)
    if (line.startsWith(">")) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith(">")) {
        quoteLines.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      elements.push(
        <blockquote key={`bq-${i}`} className="border-l-4 border-primary/40 pl-4 py-2 my-4 text-muted-foreground italic bg-muted/30 rounded-r-lg">
          {quoteLines.map((ql, qi) => (
            <p key={qi} className="my-1">{processInline(ql)}</p>
          ))}
        </blockquote>
      );
      continue;
    }

    // Table (collect consecutive | lines)
    if (line.includes("|") && line.trim().startsWith("|")) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i]?.trim().startsWith("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      // Parse: first row = header, second = separator (skip), rest = body
      const parseRow = (r: string) => r.split("|").slice(1, -1).map((c) => c.trim());
      const headerCells = parseRow(tableLines[0]);
      const bodyRows = tableLines.filter((_, idx) => {
        if (idx === 0) return false;
        const cells = parseRow(tableLines[idx]);
        return !cells.every((c) => /^[-:]+$/.test(c));
      }).map(parseRow);

      elements.push(
        <div key={`table-${i}`} className="overflow-x-auto my-4">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                {headerCells.map((cell, ci) => (
                  <th key={ci} className="border border-border p-2 bg-muted text-left font-semibold">
                    {processInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="border border-border p-2">
                      {processInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Headings
    if (line.startsWith("#### ")) {
      elements.push(<h4 key={`h4-${i}`} className="text-lg font-semibold mt-6 mb-2">{processInline(line.slice(5))}</h4>);
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      elements.push(<h3 key={`h3-${i}`} className="text-xl font-semibold mt-8 mb-3">{processInline(line.slice(4))}</h3>);
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      elements.push(<h2 key={`h2-${i}`} className="text-2xl font-display font-bold mt-10 mb-4">{processInline(line.slice(3))}</h2>);
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      elements.push(<h1 key={`h1-${i}`} className="text-3xl font-display font-bold mt-10 mb-4">{processInline(line.slice(2))}</h1>);
      i++;
      continue;
    }

    // Image
    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      elements.push(
        <figure key={`img-${i}`} className="my-6">
          <img src={imgMatch[2]} alt={imgMatch[1]} className="rounded-lg max-w-full mx-auto" loading="lazy" />
          {imgMatch[1] && <figcaption className="text-center text-sm text-muted-foreground mt-2">{imgMatch[1]}</figcaption>}
        </figure>
      );
      i++;
      continue;
    }

    // Unordered list (collect consecutive - lines)
    if (line.match(/^(\s*)[-*]\s/)) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && lines[i].match(/^(\s*)[-*]\s/)) {
        const itemText = lines[i].replace(/^(\s*)[-*]\s/, "");
        // Check for checkbox
        if (itemText.startsWith("[ ] ")) {
          listItems.push(
            <li key={`uli-${i}`} className="flex items-start gap-2 my-1">
              <input type="checkbox" disabled className="mt-1.5" />
              <span className="text-muted-foreground">{processInline(itemText.slice(4))}</span>
            </li>
          );
        } else if (itemText.startsWith("[x] ") || itemText.startsWith("[X] ")) {
          listItems.push(
            <li key={`uli-${i}`} className="flex items-start gap-2 my-1">
              <input type="checkbox" disabled checked className="mt-1.5" />
              <span className="text-muted-foreground line-through">{processInline(itemText.slice(4))}</span>
            </li>
          );
        } else {
          listItems.push(
            <li key={`uli-${i}`} className="my-1">{processInline(itemText)}</li>
          );
        }
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="ml-6 my-3 list-disc text-muted-foreground space-y-1">{listItems}</ul>);
      continue;
    }

    // Ordered list (collect consecutive numbered lines)
    if (/^\d+\.\s/.test(line)) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        const itemText = lines[i].replace(/^\d+\.\s/, "");
        listItems.push(
          <li key={`oli-${i}`} className="my-1">{processInline(itemText)}</li>
        );
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="ml-6 my-3 list-decimal text-muted-foreground space-y-1">{listItems}</ol>);
      continue;
    }

    // Paragraph
    elements.push(
      <p key={`p-${i}`} className="text-muted-foreground leading-relaxed my-3">
        {processInline(line)}
      </p>
    );
    i++;
  }

  return elements;
}
